import React, { useState } from 'react';
import { api } from '../../lib/api';

const InviteModal = ({ isOpen, onClose, onSuccess }) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [role, setRole] = useState('ARTISAN');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [inviteLink, setInviteLink] = useState(null);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await api.post('/api/admin/invite', { email, name, role });
            if (response.ok) {
                const data = await response.json();
                setInviteLink(data.invite_link);
                // Pass the link to parent for toast display
                onSuccess(email, data.invite_link);
                // Don't close immediately - show success state with link
                setTimeout(() => {
                    onClose();
                    setEmail('');
                    setName('');
                    setRole('ARTISAN');
                    setInviteLink(null);
                }, 5000); // Auto-close after 5 seconds
            } else {
                // Parse error response safely
                try {
                    const data = await response.json();
                    setError(data.message || `Error: ${response.status} ${response.statusText}`);
                } catch (parseErr) {
                    setError(`Error: ${response.status} ${response.statusText}`);
                }
            }
        } catch (err) {
            setError(err.message || "Failed to send invite");
        } finally {
            setLoading(false);
        }
    };

    const overlayStyle = {
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(18, 60, 46, 0.4)',
        backdropFilter: 'blur(4px)',
    };

    const modalStyle = {
        background: 'white',
        border: '1px solid rgba(18, 60, 46, 0.1)',
        width: '100%',
        maxWidth: '28rem',
        padding: '1.5rem',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        position: 'relative',
    };

    const closeBtnStyle = {
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        color: '#3e7140',
        width: '2rem',
        height: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '1.1rem',
        transition: 'all 0.2s',
    };

    const headingStyle = {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#123c2e',
        marginBottom: '1.5rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontFamily: 'serif',
    };

    const labelStyle = {
        display: 'block',
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: '#3e7140',
        marginBottom: '0.25rem',
        fontWeight: '600',
    };

    const inputStyle = {
        width: '100%',
        background: '#fffaf0',
        border: '1px solid rgba(18, 60, 46, 0.2)',
        borderRadius: '0.75rem',
        padding: '0.75rem',
        color: '#123c2e',
        outline: 'none',
        fontSize: '1rem',
        transition: 'all 0.2s',
        boxSizing: 'border-box',
        opacity: inviteLink !== null ? 0.5 : 1,
    };

    const errorBoxStyle = {
        background: '#fef2f2',
        border: '1px solid #fecaca',
        color: '#b91c1c',
        fontSize: '0.875rem',
        padding: '0.75rem',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
    };

    const successBoxStyle = {
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        padding: '1rem',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
    };

    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                <button
                    onClick={onClose}
                    style={closeBtnStyle}
                    onMouseOver={(e) => e.target.style.background = '#f4f0e6'}
                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                >
                    ✕
                </button>

                <h2 style={headingStyle}>Invite New Member</h2>

                {error && (
                    <div style={errorBoxStyle}>
                        {error}
                    </div>
                )}

                {inviteLink && (
                    <div style={successBoxStyle}>
                        <p style={{ color: '#166534', fontWeight: '600', marginBottom: '0.5rem' }}>✓ Invite sent successfully!</p>
                        <p style={{ fontSize: '0.75rem', color: '#15803d', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Share this link:</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="text"
                                readOnly
                                value={inviteLink}
                                style={{ ...inputStyle, flex: 1, fontSize: '0.75rem', fontFamily: 'monospace', background: 'white', border: '1px solid #86efac', opacity: 1 }}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText(inviteLink);
                                    alert('Link copied to clipboard!');
                                }}
                                style={{
                                    padding: '0.5rem 0.75rem',
                                    background: '#b48a2b',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    borderRadius: '0.5rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                                onMouseOver={(e) => e.target.style.background = '#9c7825'}
                                onMouseOut={(e) => e.target.style.background = '#b48a2b'}
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={labelStyle}>Email Address</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={inviteLink !== null}
                            style={inputStyle}
                            placeholder="colleague@glaminant.com"
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={labelStyle}>Full Name</label>
                        <input
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={inviteLink !== null}
                            style={inputStyle}
                            placeholder="John Doe"
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={labelStyle}>Role</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            disabled={inviteLink !== null}
                            style={inputStyle}
                        >
                            <option value="ARTISAN">Artisan</option>
                            <option value="ADMIN">Admin</option>
                        </select>
                    </div>

                    <div style={{ paddingTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                color: '#3e7140',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '0.75rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                            onMouseOver={(e) => { e.target.style.background = '#f4f0e6'; e.target.style.color = '#123c2e'; }}
                            onMouseOut={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#3e7140'; }}
                        >
                            {inviteLink ? 'Close' : 'Cancel'}
                        </button>
                        {!inviteLink && (
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    padding: '0.5rem 1.5rem',
                                    background: loading ? '#d4a84f' : '#b48a2b',
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    border: 'none',
                                    borderRadius: '2rem',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.7 : 1,
                                    transition: 'all 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                }}
                                onMouseOver={(e) => { if (!loading) e.target.style.background = '#9c7825'; }}
                                onMouseOut={(e) => { if (!loading) e.target.style.background = '#b48a2b'; }}
                            >
                                {loading ? 'Sending...' : 'Send Invite'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InviteModal;
